import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, downloadFile } from "@/lib/api";
import { inr, dmy, todayISO, addDays, num } from "@/lib/format";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { FieldGrid } from "@/components/Field";

export default function InvoiceNew() {
  const nav = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [settings, setSettings] = useState({});
  const [f, setF] = useState({ company_id: "", invoice_date: todayISO(), due_date: addDays(todayISO(), 30), period_from: "", period_to: "", gst_rate: 5, tax_type: "", notes: "" });
  const [duties, setDuties] = useState([]);
  const [sel, setSel] = useState({});
  const [extra, setExtra] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/settings").then((r) => { setSettings(r.data); setF((s) => ({ ...s, gst_rate: r.data.default_gst_rate ?? 5 })); });
  }, []);

  useEffect(() => {
    if (!f.company_id) return setDuties([]);
    api.get("/duties", { params: { company_id: f.company_id, unbilled: true, date_from: f.period_from, date_to: f.period_to } })
      .then((r) => { setDuties(r.data); setSel(Object.fromEntries(r.data.map((d) => [d.id, true]))); });
  }, [f.company_id, f.period_from, f.period_to]);

  const company = companies.find((c) => c.id === f.company_id);
  const change = (k, v) => setF((s) => {
    const n = { ...s, [k]: v };
    if (k === "company_id") {
      const c = companies.find((x) => x.id === v);
      if (c?.credit_days) n.due_date = addDays(s.invoice_date, c.credit_days);
      const st = (c?.gstin || "").slice(0, 2) || c?.state_code || settings.state_code;
      n.tax_type = st === (settings.state_code || "27") ? "CGST_SGST" : "IGST";
    }
    return n;
  });

  const t = useMemo(() => {
    const picked = duties.filter((d) => sel[d.id]);
    let taxable = 0, reimb = 0;
    picked.forEach((d) => { const p = num(d.toll) + num(d.parking) + num(d.state_tax); reimb += p; taxable += num(d.billing_total) - p; });
    extra.forEach((i) => { const a = num(i.qty || 1) * num(i.rate); if (i.taxable) taxable += a; else reimb += a; });
    const gst = (taxable * num(f.gst_rate)) / 100;
    const raw = taxable + reimb + gst;
    return { count: picked.length, taxable, reimb, gst, grand: Math.round(raw), round: Math.round(raw) - raw };
  }, [duties, sel, extra, f.gst_rate]);

  const save = async () => {
    if (!f.company_id) return toast.error("Select a company");
    setSaving(true);
    try {
      const body = { ...f, gst_rate: num(f.gst_rate), duty_ids: duties.filter((d) => sel[d.id]).map((d) => d.id), extra_items: extra.map((i) => ({ ...i, qty: num(i.qty || 1), rate: num(i.rate) })) };
      const { data } = await api.post("/invoices", body);
      toast.success(`Invoice ${data.invoice_no} created`);
      await downloadFile(`/invoices/${data.id}/pdf`, `${data.invoice_no.replace(/\//g, "-")}.pdf`).catch(() => {});
      nav("/invoices");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const fields = [
    { key: "company_id", label: "Bill To Company", type: "select", required: true, options: companies.map((c) => ({ value: c.id, label: c.name })), span: 2 },
    { key: "invoice_date", label: "Invoice Date", type: "date" },
    { key: "due_date", label: "Due Date", type: "date" },
    { key: "period_from", label: "Duty Period From", type: "date" },
    { key: "period_to", label: "Duty Period To", type: "date" },
    { key: "gst_rate", label: "GST Rate %", type: "select", options: ["0", "5", "12", "18"].map((v) => ({ value: v, label: `${v}%` })) },
    { key: "tax_type", label: "Tax Type", type: "select", options: [{ value: "CGST_SGST", label: "CGST + SGST (intra-state)" }, { value: "IGST", label: "IGST (inter-state)" }] },
    { key: "notes", label: "Notes on invoice", type: "textarea", span: 3, rows: 2 },
  ];
  const allOn = duties.length > 0 && duties.every((d) => sel[d.id]);

  return (
    <div data-testid="invoice-new-page">
      <Link to="/invoices" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Invoices</Link>
      <PageHeader eyebrow="Document Generator" title="New Tax Invoice" subtitle="Pick a company and the completed, unbilled duties to include." />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <Panel title="Invoice Details"><FieldGrid fields={fields} data={{ ...f, gst_rate: String(f.gst_rate) }} onChange={change} testPrefix="invoice" /></Panel>
          <Panel title={`Unbilled Completed Duties${company ? ` · ${company.name}` : ""}`}>
            {!f.company_id ? <EmptyState title="Select a company" text="Its completed, unbilled duties will appear here." /> : duties.length === 0 ? (
              <EmptyState title="No unbilled duties" text="Mark duties as Completed to bill them, or add manual line items below." />
            ) : (
              <div className="overflow-x-auto -mx-4 sm:-mx-5">
                <table className="w-full text-sm" data-testid="invoice-duties-table">
                  <thead><tr className="border-b text-left">
                    <th className="px-4 py-2.5"><Checkbox checked={allOn} onCheckedChange={(v) => setSel(Object.fromEntries(duties.map((d) => [d.id, !!v])))} data-testid="select-all-duties" /></th>
                    {["Duty", "Date", "Guest", "Vehicle", "KM", "Amount"].map((h) => <th key={h} className="px-4 py-2.5 eyebrow">{h}</th>)}
                  </tr></thead>
                  <tbody>{duties.map((d) => (
                    <tr key={d.id} className="border-b last:border-0">
                      <td className="px-4 py-2.5"><Checkbox checked={!!sel[d.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [d.id]: !!v }))} data-testid={`select-duty-${d.id}`} /></td>
                      <td className="px-4 py-2.5 font-mono text-xs">{d.duty_no}</td>
                      <td className="px-4 py-2.5">{dmy(d.duty_date)}</td>
                      <td className="px-4 py-2.5">{d.guest_name || "-"}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{d.vehicle_number}</td>
                      <td className="px-4 py-2.5 num">{d.total_km}</td>
                      <td className="px-4 py-2.5 num font-semibold">{inr(d.billing_total)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </Panel>
          <Panel title="Additional Line Items" action={<Button size="sm" variant="outline" onClick={() => setExtra([...extra, { description: "", qty: 1, rate: "", taxable: true }])} data-testid="add-line-item-btn"><Plus className="h-4 w-4 mr-1" /> Add item</Button>}>
            {extra.length === 0 ? <div className="text-sm text-muted-foreground">Optional — e.g. monthly fixed charges or adjustments.</div> : (
              <div className="space-y-3">{extra.map((it, i) => {
                const up = (k, v) => setExtra(extra.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${i}`}>
                    <Input className="col-span-12 sm:col-span-5" placeholder="Description" value={it.description} onChange={(e) => up("description", e.target.value)} data-testid={`line-item-desc-${i}`} />
                    <Input className="col-span-3 sm:col-span-1 num" type="number" placeholder="Qty" value={it.qty} onChange={(e) => up("qty", e.target.value)} />
                    <Input className="col-span-4 sm:col-span-2 num" type="number" placeholder="Rate" value={it.rate} onChange={(e) => up("rate", e.target.value)} data-testid={`line-item-rate-${i}`} />
                    <label className="col-span-3 sm:col-span-2 flex items-center gap-2 text-xs"><Checkbox checked={it.taxable} onCheckedChange={(v) => up("taxable", !!v)} /> Taxable</label>
                    <div className="col-span-1 num text-xs text-right hidden sm:block">{inr(num(it.qty || 1) * num(it.rate))}</div>
                    <Button variant="ghost" size="icon" className="col-span-2 sm:col-span-1 text-rose-600" onClick={() => setExtra(extra.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                );
              })}</div>
            )}
          </Panel>
        </div>
        <aside className="xl:sticky xl:top-24 rounded-xl border bg-slate-950 text-slate-100 p-5 shadow-lg grain" data-testid="invoice-summary">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-amber-400 mb-3">Invoice Preview</div>
          {[["Duties selected", t.count], ["Taxable value", inr(t.taxable, 2)], ["Reimbursements", inr(t.reimb, 2)],
            [f.tax_type === "IGST" ? `IGST ${f.gst_rate}%` : `CGST+SGST ${f.gst_rate}%`, inr(t.gst, 2)], ["Round off", inr(t.round, 2)]].map(([l, v]) => (
            <div key={l} className="flex justify-between py-1.5 text-sm"><span className="text-slate-400">{l}</span><span className="num">{v}</span></div>
          ))}
          <div className="border-t border-white/10 mt-2 pt-3">
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">Grand Total</div>
            <div className="font-heading text-4xl font-bold num mt-1" data-testid="invoice-grand-total">{inr(t.grand)}</div>
          </div>
          <Button onClick={save} disabled={saving || (!t.count && !extra.length)} className="w-full mt-5 h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="invoice-generate-btn">
            <FileCheck2 className="h-4 w-4 mr-1.5" />{saving ? "Generating..." : "Generate & Download PDF"}
          </Button>
        </aside>
      </div>
    </div>
  );
}
